import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { computeLandedCost, isDiscountCostLine } from "@/lib/catalog/purchaseCost";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { listPurchaseCostTypeRecords, syncPurchaseCostTypesFromApi } from "@/lib/catalog/purchaseCostTypesStore";
import {
  buildInvoiceCoveredScheduledIds,
  buildScheduledPaymentAllocations,
  fetchProductPayments,
  isActualProductPayment,
  isInvoiceLineLevelActual,
  isScheduledProductPayment,
  productPaymentErrorMessage,
  recordProductPayment,
  scheduledRowOutstanding,
  type ExtraPaymentRecord,
  type ProductPaymentRecord,
} from "@/lib/catalog/productPaymentsStore";
import {
  investorSettlementErrorMessage,
  recordInvestorSettlement,
} from "@/lib/catalog/investorSettlementStore";
import {
  catalogProductErrorMessage,
  changeProductInventoryStatus,
  deleteProduct,
  fetchProductFromApi,
  getProductById,
  type InventoryStatusValue,
  type ProductRecord,
} from "@/lib/catalog/productsStore";
import { fetchProductAdListings, type ProductAdListingRow } from "@/lib/catalog/productAdListingsStore";
import { listWarehouseRecords, syncWarehousesFromApi, type WarehouseRecord } from "@/lib/catalog/warehousesStore";
import { OlxIcon } from "@/components/icons/OlxIcon";
import { PakWheelsIcon } from "@/components/icons/PakWheelsIcon";
import { WebsiteIcon } from "@/components/icons/WebsiteIcon";
import { WiseWheelsIcon } from "@/components/icons/WiseWheelsIcon";
import { normalizeProductMediaUrlForBrowser } from "@/lib/mediaUrls";
import { getCategoryProductSchema } from "@/lib/catalog/mockCatalogApi";
import type { Attribute, AttributeValue, CategoryProductSchema, UploadedFile } from "@/lib/catalog/types";
import { cn } from "@/lib/utils";
import PermissionDenied from "@/components/PermissionDenied";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { RecordBalancePaymentDialog } from "@/components/sales/RecordBalancePaymentDialog";
import { paySaleBalance } from "@/lib/sales/salesStore";
import { GenerateGatePassDialog } from "@/components/sales/GenerateGatePassDialog";
import { formatDeliveryPayload, nowDeliveryParts, parseDeliveryParts } from "@/lib/sales/gatePass";
import { updateSaleDelivery } from "@/lib/sales/saleInvoiceStore";
import { ProductLedgerDialog } from "@/components/catalog/ProductLedgerDialog";
import { ProductPaymentDialog } from "@/components/catalog/ProductPaymentDialog";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  ListChecks,
  Loader2,
  Megaphone,
  Package,
  Paperclip,
  Printer,
  Receipt,
  RefreshCw,
  Trash2,
  Wallet,
  X,
  Ticket,
  CreditCard,
} from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { isAxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";

function money(a: any, b?: any): string {
  const n = b !== undefined ? b : a;
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return formatCurrency(Number(n));
}

function isImageDocument(file: UploadedFile): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file.filename || "");
}

function isPdfDocument(file: UploadedFile): boolean {
  const mime = (file.mimeType || "").toLowerCase();
  if (mime === "application/pdf" || mime.includes("pdf")) return true;
  return /\.pdf$/i.test(file.filename || "");
}

function documentCopyLabel(copy: UploadedFile["documentCopy"]): string {
  if (copy === "duplicate") return "Duplicate";
  if (copy === "original") return "Original";
  return "—";
}

function isOwnerSurplusSummary(summary: any | null | undefined): boolean {
  return summary?.deal_type === "owner_vehicle" && summary?.profit_rule_type === "owner_surplus";
}

function saleAmount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function optionLabel(attribute: Attribute, raw: string): string {
  const opt = attribute.options?.find((o) => o.value === raw);
  return opt?.label ?? raw;
}

function formatAttributeValueAsTags(attribute: Attribute, value: AttributeValue | undefined): { mode: "text" | "tags"; text?: string; tags?: string[] } {
  if (value == null) return { mode: "text", text: "—" };

  if (attribute.type === "select") {
    const raw = typeof value === "string" ? value : "";
    return { mode: "text", text: raw ? optionLabel(attribute, raw) : "—" };
  }

  if (attribute.type === "multiselect") {
    const raws = Array.isArray(value) ? (value as string[]) : [];
    const tags = raws.map((v) => optionLabel(attribute, v)).filter(Boolean);
    return tags.length ? { mode: "tags", tags } : { mode: "text", text: "—" };
  }

  if (Array.isArray(value)) {
    const tags = (value as unknown[]).map((v) => String(v)).filter(Boolean);
    return tags.length ? { mode: "tags", tags } : { mode: "text", text: "—" };
  }

  if (typeof value === "object") {
    return { mode: "text", text: "—" };
  }

  return { mode: "text", text: String(value) };
}

function isPriceLikeAttribute(attribute: Attribute): boolean {
  const t = `${attribute.name} ${attribute.code}`.toLowerCase();
  return /\b(price|msrp|rrp|retail|landed|asking|cost)\b/i.test(t);
}

function sortByOrder<T extends { sortOrder?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** For specification booleans: avoid `Boolean("false") === true`. */
function coerceSpecBoolean(
  v: unknown,
): "set-true" | "set-false" | "unset" {
  if (v == null) return "unset";
  if (typeof v === "boolean") {
    return v ? "set-true" : "set-false";
  }
  if (typeof v === "number" && Number.isFinite(v)) {
    if (v === 0) return "set-false";
    if (v === 1) return "set-true";
  }
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes" || s === "on") return "set-true";
    if (s === "false" || s === "0" || s === "no" || s === "off" || s === "") return "set-false";
  }
  return "unset";
}

const AD_LISTING_PLATFORM_LABEL: Record<ProductAdListingRow["platform"], string> = {
  olx: "OLX",
  "pak-wheels": "PakWheels",
  "wise-wheels": "Wise Wheels",
  website: "Website",
};

function AdListingPlatformIcon({
  platform,
  className,
}: {
  platform: ProductAdListingRow["platform"];
  className?: string;
}) {
  const cls = cn("h-4 w-4 shrink-0", className);
  if (platform === "olx") {
    return <OlxIcon className={cn(cls, "text-emerald-600 dark:text-emerald-400")} aria-hidden />;
  }
  if (platform === "pak-wheels") {
    return <PakWheelsIcon className={cls} aria-hidden />;
  }
  if (platform === "website") {
    return <WebsiteIcon className={cn(cls, "text-violet-600 dark:text-violet-400")} aria-hidden />;
  }
  return <WiseWheelsIcon className={cls} aria-hidden />;
}

function formatAdListingSalePrice(n: number | null, canViewPrices: boolean): string {
  if (!canViewPrices) return "—";
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `₨ ${Number(n).toLocaleString()}`;
}

function formatAdListingCreatedAt(iso: string): string {
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, "PPp") : iso;
  } catch {
    return iso;
  }
}

function inventoryStatusPresentation(status: string | null | undefined): {
  label: string;
  className: string;
} {
  const s = status ?? "draft";
  if (s === "sold") {
    return {
      label: "Sold",
      className: "border border-destructive/50 bg-destructive/15 text-destructive",
    };
  }
  if (s === "reserved") {
    return {
      label: "Reserved",
      className: "border border-sky-600/40 bg-sky-500/15 text-sky-800 dark:text-sky-200",
    };
  }
  if (s === "draft") {
    return {
      label: "Draft",
      className: "border border-amber-800/40 bg-amber-500/20 text-amber-950 dark:text-amber-100",
    };
  }
  if (s === "unsold") {
    return {
      label: "Unsold",
      className: "border border-orange-800/40 bg-orange-600/15 text-orange-950 dark:text-orange-100",
    };
  }
  return {
    label: "In stock",
    className: "border border-emerald-700/35 bg-emerald-600/12 text-emerald-900 dark:text-emerald-100",
  };
}

const INVENTORY_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "in_stock", label: "In stock" },
  { value: "reserved", label: "Reserved" },
  { value: "unsold", label: "Unsold" },
  { value: "sold", label: "Sold" },
] as const;

const ProductView = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  const canView = can(authUser, "product_can_view");
  const canUpdate = can(authUser, "product_can_update");
  const canDelete = can(authUser, "product_can_delete");
  const canViewPrices = can(authUser, "product_can_view_prices");
  const canCreateSale = can(authUser, "sale_can_create") || can(authUser, "product_can_view_prices");
  const [apiForbidden, setApiForbidden] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const [nextInventoryStatus, setNextInventoryStatus] = useState<InventoryStatusValue | "">("");
  const [statusReason, setStatusReason] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [schema, setSchema] = useState<CategoryProductSchema | null>(null);
  const [specGroupOpen, setSpecGroupOpen] = useState<Record<string, boolean>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [viewingDocument, setViewingDocument] = useState<UploadedFile | null>(null);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [gatePassOpen, setGatePassOpen] = useState(false);
  const [gatePassSaving, setGatePassSaving] = useState(false);
  const [gatePassDate, setGatePassDate] = useState(() => nowDeliveryParts().date);
  const [gatePassTime, setGatePassTime] = useState(() => nowDeliveryParts().time);
  const canRecordPayment = can(authUser, "sale_can_update") || can(authUser, "sale_can_create");

  const [product, setProduct] = useState<ProductRecord | undefined>(() => (id ? getProductById(id) : undefined));
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>(() => listWarehouseRecords());
  const [hydrating, setHydrating] = useState(() => Boolean(id && !getProductById(id)));
  /** True while GET /catalog/products/{id} is in flight (needed so sold products can show a sale-record spinner before `latest_sale` arrives). */
  const [productDetailFetching, setProductDetailFetching] = useState(() => {
    if (!id) return false;
    const loc = getProductById(id);
    return Boolean(loc && (loc.inventoryStatus ?? "in_stock") === "sold" && !loc.latestSale);
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setAuthUser(getStoredAuthUser());
    };
    const onAuth = () => setAuthUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!canView) return;
    let mounted = true;
    void syncWarehousesFromApi({ activeOnly: false })
      .then(() => {
        if (mounted) setWarehouses(listWarehouseRecords());
      })
      .catch(() => {
        if (mounted) setWarehouses(listWarehouseRecords());
      });
    return () => {
      mounted = false;
    };
  }, [canView, id]);

  useEffect(() => {
    if (!id) {
      setProduct(undefined);
      setHydrating(false);
      setApiForbidden(false);
      return;
    }
    if (!canView) {
      setProduct(undefined);
      setHydrating(false);
      setApiForbidden(false);
      return;
    }

    let cancelled = false;
    const local = getProductById(id);
    setApiForbidden(false);
    setProductDetailFetching(true);

    // Show cached row immediately, but always refetch: list/sync payloads omit `latest_sale`,
    // which is only returned from GET /catalog/products/{id} (e.g. when sold).
    if (local) {
      setProduct(local);
      setHydrating(false);
    } else {
      setProduct(undefined);
      setHydrating(true);
    }

    void fetchProductFromApi(id)
      .then((rec) => {
        if (cancelled) return;
        if (rec) {
          setProduct(rec);
        } else if (!local) {
          setProduct(undefined);
        }
        setHydrating(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isAxiosError(e) && e.response?.status === 403) {
          setApiForbidden(true);
          setProduct(undefined);
        } else if (!local) {
          setProduct(undefined);
        }
        setHydrating(false);
      })
      .finally(() => {
        if (!cancelled) {
          setProductDetailFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, canView]);

  const reloadProduct = () => {
    if (!id) return Promise.resolve();
    return fetchProductFromApi(id).then((rec) => {
      if (rec) setProduct(rec);
    });
  };

  const openGatePassModal = () => {
    if (!product?.latestSale) return;
    const parts = parseDeliveryParts(product.latestSale.delivery_date);
    setGatePassDate(parts.date);
    setGatePassTime(parts.time);
    setGatePassOpen(true);
  };

  const submitGatePass = async () => {
    if (!product?.latestSale) return;
    const payload = formatDeliveryPayload(gatePassDate, gatePassTime);
    if (!payload) {
      toast.error("Delivery date and time are required.");
      return;
    }
    setGatePassSaving(true);
    try {
      const r = await updateSaleDelivery(product.latestSale.id, payload);
      toast.success(r.message);
      await reloadProduct();
      setGatePassOpen(false);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to save delivery date.");
    } finally {
      setGatePassSaving(false);
    }
  };

  const [purchaseCostTypesTick, setPurchaseCostTypesTick] = useState(0);
  const costTypes = useMemo(() => listPurchaseCostTypeRecords(), [purchaseCostTypesTick]);
  const costTypeLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of costTypes) m.set(String(t.id), t.label);
    return m;
  }, [costTypes]);

  const [productAdListings, setProductAdListings] = useState<ProductAdListingRow[]>([]);
  const [adListingsLoading, setAdListingsLoading] = useState(false);
  const [productPayments, setProductPayments] = useState<ProductPaymentRecord[]>([]);
  const [extraPayments, setExtraPayments] = useState<ExtraPaymentRecord[]>([]);
  const [paymentsMeta, setPaymentsMeta] = useState({ total_debit: 0, total_credit: 0, net: 0 });
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payDialogOutstanding, setPayDialogOutstanding] = useState(0);
  const [payDialogAction, setPayDialogAction] = useState<"pay" | "receive">("pay");
  const [payDialogPartyName, setPayDialogPartyName] = useState("");
  const [payDialogPartyId, setPayDialogPartyId] = useState(0);
  const [payDialogTransactionType, setPayDialogTransactionType] = useState("");
  const [payDialogScheduledPaymentId, setPayDialogScheduledPaymentId] = useState(0);
  const [payDialogInvoiceId, setPayDialogInvoiceId] = useState<number | null>(null);
  const [payDialogCurrencyCode, setPayDialogCurrencyCode] = useState<string | null>(null);
  const [payDialogCurrencyAmount, setPayDialogCurrencyAmount] = useState<number | null>(null);
  const [payDialogExchangeRate, setPayDialogExchangeRate] = useState<number | null>(null);
  const [payDialogInvoiceNo, setPayDialogInvoiceNo] = useState<string | null>(null);
  const [payDialogInvoiceDate, setPayDialogInvoiceDate] = useState<string | null>(null);
  const [payDialogInvoiceDueDate, setPayDialogInvoiceDueDate] = useState<string | null>(null);
  const [payDialogInvoiceCostItems, setPayDialogInvoiceCostItems] = useState<Array<{
    label: string;
    currencyAmount: number | null;
    currencyCode: string | null;
    amount: number;
  }> | undefined>(undefined);
  const [payDialogAllowExcess, setPayDialogAllowExcess] = useState(true);

  const [payDialogProfitDistributionId, setPayDialogProfitDistributionId] = useState<number | null>(null);

  const handleRecordProductPayment = async (payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    funding_source?: "cash_bank" | "investor_balance";
    bank_id?: number | null;
    bank_name?: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    cheque_no?: string | null;
    deposit_slip_no?: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id: number;
    transaction_type?: string | null;
    scheduled_payment_id?: number | null;
    product_invoice_id?: number | null;
    currency_code?: string | null;
    currency_amount?: number | null;
    exchange_rate?: number | null;
    settlement_mode?: "cash_pay" | "reinvest";
    profit_distribution_id?: number | null;
    destination_product_id?: number | null;
    destination_description?: string | null;
  }) => {
    if (!id) return;
    try {
      if (payload.settlement_mode === "reinvest") {
        if (!payload.profit_distribution_id) {
          throw new Error("Profit distribution is required for reinvestment.");
        }
        await recordInvestorSettlement(id, {
          profit_distribution_id: Number(payload.profit_distribution_id),
          refund_amount: 0,
          reinvest_amount: payload.amount,
          destination_description: payload.destination_description?.trim() || null,
          destination_product_id: null,
          allocated_at: payload.payment_date,
          notes: payload.notes,
        });
        toast.success("Investor reinvestment recorded. Payable cleared and debit/credit entries saved.");
      } else {
        // Cash / bank from product view: product payment + PD only — no investor_investment_allocations.
        await recordProductPayment(id, payload);
        toast.success("Payment recorded successfully.");
      }

      // Reload payments list
      setPaymentsLoading(true);
      const res = await fetchProductPayments(id);
      setProductPayments(res.data);
      setExtraPayments(res.extra_payments);
      setPaymentsMeta(res.meta);
      setPaymentsLoading(false);

      // Reload product details (updates latest sale & profit distributions)
      const refreshedProduct = await fetchProductFromApi(id);
      if (refreshedProduct) {
        setProduct(refreshedProduct);
      }
    } catch (e: any) {
      const msg = investorSettlementErrorMessage(e) || productPaymentErrorMessage(e);
      toast.error(msg);
      throw e;
    }
  };

  useEffect(() => {
    void syncPurchaseCostTypesFromApi().then(() => setPurchaseCostTypesTick((t) => t + 1));
  }, []);

  useEffect(() => {
    if (!id || !canView || !canViewPrices) {
      setProductPayments([]);
      setExtraPayments([]);
      setPaymentsMeta({ total_debit: 0, total_credit: 0, net: 0 });
      return;
    }
    let cancelled = false;
    setPaymentsLoading(true);
    void fetchProductPayments(id)
      .then((res) => {
        if (cancelled) return;
        setProductPayments(res.data);
        setExtraPayments(res.extra_payments);
        setPaymentsMeta(res.meta);
      })
      .catch((e) => {
        console.error("[ProductView] product payments load failed", e);
        if (!cancelled) {
          setProductPayments([]);
          setExtraPayments([]);
          setPaymentsMeta({ total_debit: 0, total_credit: 0, net: 0 });
          if (!(isAxiosError(e) && e.response?.status === 403)) {
            toast.error(productPaymentErrorMessage(e));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setPaymentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, canView, canViewPrices, product?.id]);

  const getInvoiceAmounts = (
    inv: typeof product.invoices[number],
    mode: "pay" | "receive" = "pay",
  ) => {
    // Billed = collective invoice amount (net of linked purchase discount)
    const billed = Number(inv.companyCurrencyAmount ?? 0);
    let paid = 0;

    const investorPartyIds = new Set(
      (product.dealPartners ?? [])
        .map((dp) => (dp.partyId != null && String(dp.partyId).trim() !== "" ? String(dp.partyId) : null))
        .filter((id): id is string => Boolean(id)),
    );

    (productPayments || []).forEach((p) => {
      if (p.reference_type !== "journal_entry") return;
      if (String(p.product_invoice_id) !== String(inv.id)) return;

      if (mode === "pay") {
        // Supplier invoice payments are money-out (debit).
        if (String(p.party_id) === String(inv.partyId) && Number(p.debit) > Number(p.credit)) {
          paid += Number(p.debit);
        }
        return;
      }

      // Receivables: investor funding received against this invoice (credit).
      const isInvestorParty =
        investorPartyIds.has(String(p.party_id)) || p.transaction_type === "investment";
      if (isInvestorParty && Number(p.credit) > Number(p.debit)) {
        paid += Number(p.credit);
      } else if (
        // Fallback: any credit linked to this invoice that is not a supplier pay-out.
        Number(p.credit) > Number(p.debit) &&
        String(p.party_id) !== String(inv.partyId)
      ) {
        paid += Number(p.credit);
      }
    });

    return {
      billed,
      paid,
      outstanding: Math.max(0, billed - paid),
    };
  };

  useEffect(() => {
    if (!product) return;
    getCategoryProductSchema(product.categoryId)
      .then(setSchema)
      .catch(() => setSchema(null));
  }, [product?.categoryId]);

  useEffect(() => {
    const groups = sortByOrder(schema?.groups ?? []);
    setSpecGroupOpen((prev) => {
      const next: Record<string, boolean> = {};
      for (const [idx, g] of groups.entries()) {
        next[g.id] = typeof prev[g.id] === "boolean" ? prev[g.id] : idx === 0;
      }
      return next;
    });
  }, [schema?.category?.id]);

  useEffect(() => {
    if (!id || !canView) return;
    let cancelled = false;
    setAdListingsLoading(true);
    void fetchProductAdListings()
      .then((rows) => {
        if (cancelled) return;
        setProductAdListings(rows.filter((r) => r.productId === id));
      })
      .catch((e) => {
        console.error("[ProductView] ad listings load failed", e);
        if (!cancelled) setProductAdListings([]);
      })
      .finally(() => {
        if (!cancelled) setAdListingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, canView]);

  if (!id) {
    return (
      <Layout title="Product">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing product id.</div>
      </Layout>
    );
  }

  if (!canView || apiForbidden) {
    return (
      <PermissionDenied
        title="Product access required"
        message="You don’t have permission to view this product. Ask an administrator to grant product_can_view."
        backTo="/products"
        backLabel="Back to products"
      />
    );
  }

  if (hydrating) {
    return (
      <Layout title="Product">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading product…
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout title="Product not found">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Product not found.</div>
          <Link to="/products">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to products
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const landed = canViewPrices ? product.landedCost ?? computeLandedCost(product.purchasePrice, product.purchaseCostLines) : null;
  const basePriceDisplay =
    product.purchasePrice != null && Number.isFinite(Number(product.purchasePrice)) ? Number(product.purchasePrice) : null;
  const purchaseLines = product.purchaseCostLines ?? [];
  const isConsignment = product.acquisitionType === "consignment";

  const purchaseCostLineTitle = (line: (typeof purchaseLines)[number]): string => {
    const fromApi = line.typeLabel?.trim();
    if (fromApi) return fromApi;
    const fromCatalog = costTypeLabel.get(String(line.typeId))?.trim();
    if (fromCatalog) return fromCatalog;
    const id = String(line.typeId ?? "").trim();
    return id || "Cost";
  };
  const productSold = (product.inventoryStatus ?? "in_stock") === "sold";
  const saleRecordLoading = productSold && !product.latestSale && productDetailFetching;
  const inv = inventoryStatusPresentation(product.inventoryStatus);
  const locationMetaLabel = (() => {
    const wid = product.warehouseId;
    if (wid == null || String(wid).trim() === "") return "—";
    const w = warehouses.find((x) => String(x.id) === String(wid));
    if (!w) return `Warehouse #${wid}`;
    const city = w.city?.trim();
    return city ? `${w.name} — ${city}` : w.name;
  })();
  const lightboxImages = (product.productImages ?? []).filter((u) => Boolean(u.url));
  const activeLightbox = lightboxImages[lightboxIndex];

  return (
    <Layout title={product.name}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Link to="/products">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="min-w-0">
              <div className="flex flex-wrap items-start gap-2 gap-y-2">
                <h1 className="min-w-0 flex-1 text-2xl font-display font-bold leading-tight text-foreground truncate">
                  {product.name}
                </h1>
                <span
                  className={cn(
                    "shrink-0 rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] shadow-sm",
                    inv.className,
                  )}
                >
                  {inv.label}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1 truncate">
                Slug: <span className="text-foreground/80">{product.slug}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Product money flow"
                  onClick={() => setLedgerModalOpen(true)}
                >
                  <BookOpen className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Product money flow</TooltipContent>
            </Tooltip>

            {canUpdate ? (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Change inventory status"
                    onClick={() => {
                      setNextInventoryStatus("");
                      setStatusReason("");
                      setStatusModalOpen(true);
                    }}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Change Inventory Status</TooltipContent>
              </Tooltip>
            ) : null}

            {canCreateSale && (product.inventoryStatus ?? "") === "in_stock" ? (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Link to={`/product-sales/new?product_id=${encodeURIComponent(product.id)}`}>
                    <Button variant="outline" size="icon" aria-label="Create sale">
                      <Receipt className="h-4 w-4" />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="top">Create Sale</TooltipContent>
              </Tooltip>
            ) : null}

            {canUpdate ? (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Link to={`/products/${product.id}/edit`}>
                    <Button variant="glass" size="icon" aria-label="Edit">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="top">Edit Vehicle</TooltipContent>
              </Tooltip>
            ) : null}

            {canDelete && !productSold ? (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10"
                    aria-label="Delete"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Delete Product</TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </div>

        <ProductLedgerDialog
          open={ledgerModalOpen}
          productId={product.id}
          onOpenChange={setLedgerModalOpen}
        />

        <ProductPaymentDialog
          open={payDialogOpen}
          onOpenChange={setPayDialogOpen}
          productId={product.id}
          outstanding={payDialogOutstanding}
          action={payDialogAction}
          partyName={payDialogPartyName}
          partyId={payDialogPartyId}
          transactionType={payDialogTransactionType}
          scheduledPaymentId={payDialogScheduledPaymentId}
          productInvoiceId={payDialogInvoiceId}
          currencyCode={payDialogCurrencyCode}
          currencyAmount={payDialogCurrencyAmount}
          exchangeRate={payDialogExchangeRate}
          invoiceNo={payDialogInvoiceNo}
          invoiceDate={payDialogInvoiceDate}
          invoiceDueDate={payDialogInvoiceDueDate}
          invoiceCostItems={payDialogInvoiceCostItems}
          acquisitionType={product.acquisitionType}
          allowExcess={payDialogAllowExcess}
          profitDistributionId={payDialogProfitDistributionId}
          onSubmit={handleRecordProductPayment}
        />

        <Dialog
          open={statusModalOpen}
          onOpenChange={(open) => {
            if (statusSaving) return;
            setStatusModalOpen(open);
            if (!open) {
              setNextInventoryStatus("");
              setStatusReason("");
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Inventory Status</DialogTitle>
              <DialogDescription>
                Current status:{" "}
                <span className="font-medium text-foreground">
                  {inventoryStatusPresentation(product.inventoryStatus).label}
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-2">
                <Label htmlFor="inventory-status-select">New Inventory Status</Label>
                <Select
                  value={nextInventoryStatus || undefined}
                  onValueChange={(v) => setNextInventoryStatus(v as InventoryStatusValue)}
                  disabled={statusSaving}
                >
                  <SelectTrigger id="inventory-status-select" className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_STATUS_OPTIONS.filter(
                      (opt) => opt.value !== (product.inventoryStatus ?? "draft"),
                    ).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inventory-status-reason">
                  Reason <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="inventory-status-reason"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Why is the status changing?"
                  rows={3}
                  disabled={statusSaving}
                  className="resize-y"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={statusSaving}
                onClick={() => setStatusModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={statusSaving || !nextInventoryStatus}
                onClick={() => {
                  if (!nextInventoryStatus) return;
                  void (async () => {
                    setStatusSaving(true);
                    try {
                      const updated = await changeProductInventoryStatus(
                        product.id,
                        nextInventoryStatus,
                        statusReason,
                      );
                      setProduct(updated);
                      setStatusModalOpen(false);
                      setNextInventoryStatus("");
                      setStatusReason("");
                      toast.success("Inventory status updated.");
                    } catch (e) {
                      toast.error(catalogProductErrorMessage(e));
                    } finally {
                      setStatusSaving(false);
                    }
                  })();
                }}
              >
                {statusSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save status"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {productSold ? (
          <div
            role="status"
            className="rounded-xl border-2 border-destructive/55 bg-gradient-to-r from-destructive/12 via-destructive/8 to-transparent px-4 py-4 shadow-sm"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 inline-flex items-center justify-center rounded-lg bg-destructive px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-destructive-foreground shadow-sm">
                  Sold
                </span>
                <p className="text-sm sm:text-base font-semibold text-foreground leading-snug">
                  This product is off the market — inventory status is <span className="text-destructive">sold</span>.
                </p>
              </div>
              <p className="text-xs font-medium text-muted-foreground sm:text-right shrink-0">Single-unit sale — no further sales for this listing.</p>
            </div>
          </div>
        ) : null}

        {adListingsLoading || productAdListings.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-border/60 bg-card/85 shadow-sm ring-1 ring-border/35">
            <div className="flex flex-col gap-3 border-b border-border/50 bg-gradient-to-r from-muted/50 via-muted/25 to-transparent px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/30 bg-primary/10">
                  <Megaphone className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">Marketplace listings</h2>
                  <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
                    Ads linked from the catalog (OLX, PakWheels, Wise Wheels). Sale amounts follow your price permissions.
                  </p>
                </div>
              </div>
              {adListingsLoading ? (
                <span className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Loading…
                </span>
              ) : (
                <span className="shrink-0 rounded-md border border-border/60 bg-background/60 px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground">
                  {productAdListings.length} listing{productAdListings.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            {adListingsLoading && productAdListings.length === 0 ? (
              <div className="flex justify-center py-14">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/35 text-left">
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Platform</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sale price</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Listing link</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/55">
                    {productAdListings.map((row) => (
                      <tr key={row.id} className="bg-background/30 transition-colors hover:bg-muted/25">
                        <td className="px-4 py-3 align-middle">
                          <span className="inline-flex items-center gap-2 font-medium text-foreground">
                            <AdListingPlatformIcon platform={row.platform} />
                            {AD_LISTING_PLATFORM_LABEL[row.platform]}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span className="inline-flex items-center rounded-md border border-emerald-600/35 bg-emerald-500/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                            Posted
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle font-medium tabular-nums text-foreground">
                          {formatAdListingSalePrice(row.salePrice, canViewPrices)}
                        </td>
                        <td className="max-w-[14rem] px-4 py-3 align-middle">
                          {row.platformLink ? (
                            <a
                              href={row.platformLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex max-w-full items-center gap-1.5 truncate text-xs font-medium text-primary underline-offset-2 hover:underline"
                            >
                              <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                              <span className="truncate">Open listing</span>
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 align-middle text-xs tabular-nums text-muted-foreground">
                          {formatAdListingCreatedAt(row.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-2">


                {lightboxImages.length > 0 ? (
                  <div className="space-y-2">
                    {/* Main Image View */}
                    <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full rounded-xl overflow-hidden border border-border/45 bg-black group">
                      <button
                        type="button"
                        className="w-full h-full cursor-zoom-in"
                        onClick={() => {
                          setLightboxIndex(activeImageIndex);
                          setLightboxOpen(true);
                        }}
                      >
                        <img
                          src={normalizeProductMediaUrlForBrowser(lightboxImages[activeImageIndex].url) ?? lightboxImages[activeImageIndex].url}
                          alt={lightboxImages[activeImageIndex].filename}
                          className="w-full h-full object-contain"
                        />
                      </button>

                      {/* Overlays: Arrows (Only show if > 1 image) */}
                      {lightboxImages.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white p-2 hover:bg-black/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveImageIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
                            }}
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white p-2 hover:bg-black/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveImageIndex((i) => (i + 1) % lightboxImages.length);
                            }}
                            aria-label="Next image"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </>
                      )}

                      {/* Page Counter Overlay */}
                      <span className="absolute bottom-3 right-3 bg-black/65 text-white text-xs px-2.5 py-1 rounded-full font-medium tabular-nums">
                        {activeImageIndex + 1} / {lightboxImages.length}
                      </span>
                    </div>

                    {/* Thumbnail Row */}
                    {lightboxImages.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-thin scrollbar-thumb-muted">
                        {lightboxImages.map((img, idx) => {
                          const isActive = idx === activeImageIndex;
                          return (
                            <button
                              key={img.id}
                              type="button"
                              className={cn(
                                "h-14 w-20 sm:h-16 sm:w-24 shrink-0 rounded-lg overflow-hidden border bg-muted/20 relative transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                isActive
                                  ? "border-primary ring-2 ring-primary/20 scale-[0.98]"
                                  : "border-border/60 hover:border-foreground/30 hover:opacity-90"
                              )}
                              onClick={() => setActiveImageIndex(idx)}
                            >
                              <img
                                src={normalizeProductMediaUrlForBrowser(img.url) ?? img.url}
                                alt={img.filename}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-border/50 aspect-[16/9] flex flex-col items-center justify-center text-muted-foreground gap-2 p-6">
                    <ImageIcon className="h-8 w-8 opacity-65" />
                    <span className="text-sm">No images uploaded for this product</span>
                  </div>
                )}

            {schema && (
              <div className="space-y-2">
                {sortByOrder(schema.groups).map((g) => {
                  const open = specGroupOpen[g.id] === true;
                  const attrs = sortByOrder(g.attributes);
                  return (
                    <div key={g.id} className="rounded-xl border border-border/50 bg-background/35 overflow-hidden">
                      <button
                        type="button"
                        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-muted/20 transition-colors"
                        onClick={() => setSpecGroupOpen((m) => ({ ...m, [g.id]: !open }))}
                        aria-expanded={open}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{g.name}</p>
                          {g.description?.trim() ? (
                            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{g.description}</p>
                          ) : null}
                        </div>
                        <div className="shrink-0 flex items-center">
                          {open ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
                          )}
                        </div>
                      </button>

                      <div
                        className={cn(
                          "grid",
                          open
                            ? "grid-rows-[1fr] transition-[grid-template-rows] duration-300"
                            : "grid-rows-[0fr] transition-[grid-template-rows] duration-200",
                        )}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="border-t border-border/40 px-4 py-4">
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
                              {attrs.map((a) => {
                                const v = product.attributes?.[a.code];
                                const rendered =
                                  !canViewPrices && isPriceLikeAttribute(a)
                                    ? { mode: "text" as const, text: "—" }
                                    : formatAttributeValueAsTags(a, v);

                                if (a.type === "boolean") {
                                  const bool = !canViewPrices && isPriceLikeAttribute(a) ? "unset" : coerceSpecBoolean(v);
                                  return (
                                    <div key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-border/40 bg-background/30 px-3 py-2">
                                      <dt className="text-xs font-medium tracking-wide text-muted-foreground">
                                        {a.name}
                                      </dt>
                                      <dd className="shrink-0">
                                        {bool === "set-true" ? (
                                          <span
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-emerald-600/35 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200"
                                            aria-label="Yes"
                                            title="Yes"
                                          >
                                            <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                                          </span>
                                        ) : (
                                          <span
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-rose-600/30 bg-rose-500/10 text-rose-700 dark:text-rose-200"
                                            aria-label={bool === "set-false" ? "No" : "Not set"}
                                            title={bool === "set-false" ? "No" : "Not set"}
                                          >
                                            <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
                                          </span>
                                        )}
                                      </dd>
                                    </div>
                                  );
                                }

                                const title =
                                  rendered.mode === "tags"
                                    ? (rendered.tags ?? []).join(", ")
                                    : (rendered.text ?? "—");
                                return (
                                  <div key={a.id} className="rounded-lg border border-border/40 bg-background/30 px-3 py-2">
                                    <dt className="text-xs font-medium tracking-wide text-muted-foreground">
                                      {a.name}
                                    </dt>
                                    <dd className="mt-1 text-[13px] font-normal text-foreground/90" title={title}>
                                      {rendered.mode === "tags" ? (
                                        <div className="flex flex-wrap gap-2">
                                          {(rendered.tags ?? []).map((t) => (
                                            <span
                                              key={t}
                                              className="rounded-full border border-border/60 bg-muted/15 px-2.5 py-0.5 text-[11px] font-normal leading-tight text-foreground/90"
                                            >
                                              {t}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="break-words">{rendered.text ?? "—"}</span>
                                      )}
                                    </dd>
                                  </div>
                                );
                              })}
                            </dl>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
              <div className="flex items-center gap-2.5 border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-5 py-3.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/50 bg-background/60">
                  <FileText className="h-4 w-4 text-primary" aria-hidden />
                </div>
                <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Description</h3>
              </div>
              <div className="p-5 sm:p-6">
                <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
                  {product.description?.trim() ? product.description : "—"}
                </p>
              </div>
            </div>

            {product.productDocuments && product.productDocuments.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
                <div className="flex items-center justify-between gap-2 border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/50 bg-background/60">
                      <Paperclip className="h-4 w-4 text-primary" aria-hidden />
                    </div>
                    <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Documents</h3>
                  </div>
                  <span className="shrink-0 rounded-full border border-border/50 bg-background/50 px-2.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                    {product.productDocuments.length}
                  </span>
                </div>
                <div className="p-5 sm:p-6 overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="border-b border-border/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="py-2 pr-2 font-medium w-12">Sr. #</th>
                        <th className="py-2 pr-2 font-medium">File</th>
                        <th className="py-2 pr-2 font-medium">Type</th>
                        <th className="py-2 pr-2 font-medium">Document Copy</th>
                        <th className="py-2 pr-2 font-medium whitespace-nowrap">No. of Pages</th>
                        <th className="py-2 pr-2 font-medium whitespace-nowrap">Size</th>
                        <th className="py-2 font-medium text-right w-24"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.productDocuments.map((u, index) => (
                        <tr key={u.id} className="border-b border-border/30 last:border-0">
                          <td className="py-2.5 pr-2 tabular-nums text-muted-foreground">{index + 1}</td>
                          <td className="py-2.5 pr-2 text-foreground/90 max-w-[180px] truncate font-medium" title={u.filename}>
                            <button
                              type="button"
                              className="truncate text-left hover:text-primary hover:underline disabled:no-underline disabled:opacity-70"
                              disabled={!u.url}
                              onClick={() => u.url && setViewingDocument(u)}
                            >
                              {u.filename}
                            </button>
                          </td>
                          <td className="py-2.5 pr-2 text-foreground/90 max-w-[140px] truncate">
                            {u.documentTypeName?.trim() || "—"}
                          </td>
                          <td className="py-2.5 pr-2 capitalize text-foreground/90">
                            {documentCopyLabel(u.documentCopy)}
                          </td>
                          <td className="py-2.5 pr-2 tabular-nums text-foreground/90">
                            {u.numberOfPages == null ? "—" : u.numberOfPages}
                          </td>
                          <td className="py-2.5 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                            {Math.round(u.size / 1024)} KB
                          </td>
                          <td className="py-2.5 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 text-xs text-primary"
                              disabled={!u.url}
                              onClick={() => u.url && setViewingDocument(u)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {/* Deal & Acquisition Card */}
            <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
              <div className="border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-4 py-3 flex items-center justify-between gap-2">
                <div>
                  <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Deal & Acquisition</h3>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Deal type, origin, and partners</p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                  <span className={cn(
                    "rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-xs",
                    product.acquisitionType === "consignment"
                      ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                      : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                  )}>
                    {product.acquisitionType === "consignment" ? "Consignment" : "Purchased"}
                  </span>
                  <span className={cn(
                    "rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-xs",
                    product.dealType === "owner_vehicle"
                      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                      : product.dealType === "partnership"
                      ? "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20"
                      : "bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-500/20"
                  )}>
                    {product.dealType === "owner_vehicle"
                      ? "Investor Vehicle"
                      : product.dealType === "partnership"
                      ? "Partnership"
                      : "Company Owned"}
                  </span>
                  {product.purchaseType && !isConsignment && (
                    <span className={cn(
                      "rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-xs",
                      product.purchaseType === "import"
                        ? "bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20"
                        : "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20"
                    )}>
                      {product.purchaseType === "import" ? "Import" : "Local"}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3 p-3.5 text-xs">
                {/* 4-Column Across Metadata Grid (Label on left, Value on right) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {!isConsignment && product.supplierName && (
                    <div className={cn("flex items-center justify-between gap-2 min-w-0 py-0.5", product.dealType !== "owner_vehicle" && !product.importData?.importCountryName ? "sm:col-span-2" : "")}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Supplier</span>
                      <span className="font-medium text-foreground truncate text-right" title={product.supplierName}>{product.supplierName}</span>
                    </div>
                  )}

                  {product.dealType === "owner_vehicle" && (
                    <div className="flex items-center justify-between gap-2 min-w-0 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
                        {isConsignment ? "Owner / Investor" : "Owner / Seller"}
                      </span>
                      <span className="font-medium text-foreground truncate text-right" title={product.dealPartners?.[0]?.partyName || "—"}>
                        {product.dealPartners?.[0]?.partyName || "—"}
                      </span>
                    </div>
                  )}

                  {product.importData?.importCountryName && (
                    <div className="flex items-center justify-between gap-2 min-w-0 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Country</span>
                      <span className="font-medium text-foreground truncate text-right">{product.importData.importCountryName}</span>
                    </div>
                  )}

                  {product.importData?.lotNumber && (
                    <div className="flex items-center justify-between gap-2 min-w-0 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Lot No.</span>
                      <span className="font-medium text-foreground truncate text-right">{product.importData.lotNumber}</span>
                    </div>
                  )}

                  {product.importData?.auctionHouse && (
                    <div className="flex items-center justify-between gap-2 min-w-0 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Auction House</span>
                      <span className="font-medium text-foreground truncate text-right" title={product.importData.auctionHouse}>{product.importData.auctionHouse}</span>
                    </div>
                  )}

                  {product.importData?.billOfLadingNumber && (
                    <div className="flex items-center justify-between gap-2 min-w-0 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">B/L No.</span>
                      <span className="font-medium text-foreground truncate text-right">{product.importData.billOfLadingNumber}</span>
                    </div>
                  )}

                  {product.importData?.auctionDate && (
                    <div className="flex items-center justify-between gap-2 min-w-0 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Auction Date</span>
                      <span className="font-medium text-foreground truncate text-right">{product.importData.auctionDate}</span>
                    </div>
                  )}
                </div>

                {/* Partners & Shares (Compact Chips) */}
                {product.dealType === "partnership" && product.dealPartners && product.dealPartners.length > 0 && (
                  <div className="space-y-1.5 pt-2 border-t border-border/35">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground block">
                      Partners ({product.dealPartners.length})
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {product.dealPartners.map((dp, idx) => (
                        <div
                          key={dp.id || idx}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border/40 bg-muted/20 px-2.5 py-1 text-xs"
                        >
                          <span className="font-medium text-foreground">{dp.partyName || `Partner #${dp.partyId}`}</span>
                          <span className="text-muted-foreground/60">·</span>
                          <span className="font-semibold tabular-nums text-foreground">
                            {dp.investmentAmount != null ? money(dp.investmentAmount) : "—"}
                            {dp.investmentRatio != null ? ` (${dp.investmentRatio}%)` : ""}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {canViewPrices ? (
              <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
                <div className="border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-5 py-3.5">
                  <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Purchase ({getActiveCompanyCurrency().code})</h3>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Base price, itemized costs, and total cost</p>
                </div>
                <div className="space-y-1 p-2 text-sm">
                  <div className="flex justify-between items-start gap-4 rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5">
                    <span className="shrink-0 text-muted-foreground">Base price</span>
                    <div className="min-w-0 text-right">
                      <span className="font-medium tabular-nums text-foreground block">
                        {formatCurrency(basePriceDisplay)}
                      </span>
                      {product.purchaseCurrencyAmount != null && product.purchaseCurrency ? (
                        <span className="text-[11px] text-muted-foreground tabular-nums block">
                          {isConsignment ? "Amount" : "Purchased"}: {formatCurrency(product.purchaseCurrencyAmount, product.purchaseCurrency)} @ {product.purchaseExchangeRate ?? 1}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {purchaseLines.map((l, idx) => {
                    const invoice = (product.invoices ?? []).find(
                      (inv) => String(inv.id) === String(l.invoiceId)
                    );
                    const hasSecondRow = Boolean(
                      invoice ||
                      l.description?.trim() ||
                      (l.currencyAmount != null && l.currencyCode)
                    );

                    return (
                      <div
                        key={`${l.typeId}-${idx}`}
                        className="rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5 space-y-1"
                      >
                        {/* Row 1: Title & Amount */}
                        <div className="flex justify-between items-center gap-4">
                          <span className="min-w-0 truncate text-muted-foreground font-medium" title={purchaseCostLineTitle(l)}>
                            {purchaseCostLineTitle(l)}
                          </span>
                          <span className={cn("min-w-0 text-right shrink-0 font-medium tabular-nums", isDiscountCostLine(l) ? "text-emerald-500 font-semibold" : "text-foreground")}>
                            {isDiscountCostLine(l) ? `-${formatCurrency(l.amount)}` : formatCurrency(l.amount)}
                          </span>
                        </div>

                        {/* Row 2: Metadata / Description (Left) & Currency Conversion (Right) */}
                        {hasSecondRow && (
                          <div className="flex justify-between items-center gap-3 text-xs">
                            <div className="min-w-0 flex items-center gap-1.5 truncate">
                              {invoice && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary shrink-0">
                                  <FileText className="w-3 h-3" />
                                  <span className="truncate">Invoice: {invoice.invoiceNo}</span>
                                </span>
                              )}
                              {invoice && l.description?.trim() && (
                                <span className="text-muted-foreground/40 shrink-0">·</span>
                              )}
                              {l.description?.trim() && (
                                <span className="truncate text-muted-foreground text-[11px]" title={l.description.trim()}>
                                  {l.description.trim()}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0 text-right shrink-0">
                              {l.currencyAmount != null && l.currencyCode ? (
                                <span className="text-[11px] text-muted-foreground tabular-nums block">
                                  {formatCurrency(l.currencyAmount, l.currencyCode)} @ {l.exchangeRate ?? 1}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex justify-between gap-4 rounded-xl border border-primary/15 bg-primary/[0.06] px-3.5 py-2.5">
                    <span className="shrink-0 font-medium text-muted-foreground">Total cost</span>
                    <span className="min-w-0 text-right font-semibold tabular-nums text-foreground">
                      {formatCurrency(landed)}
                    </span>
                  </div>
                  {product.salePrice != null && Number.isFinite(Number(product.salePrice)) ? (
                    <div className="flex justify-between gap-4 rounded-xl border border-violet-500/20 bg-violet-500/[0.06] px-3.5 py-2.5">
                      <span className="shrink-0 font-medium text-muted-foreground">Sale price</span>
                      <div className="min-w-0 text-right">
                        <span className="font-semibold tabular-nums text-foreground">
                          {formatCurrency(Number(product.salePrice))}
                        </span>
                        <p className="text-xs font-medium text-primary mt-0.5">
                          {pkrAmountShortLabel(Number(product.salePrice))}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
                {product.purchaseNotes?.trim() ? (
                  <div className="border-t border-border/40 px-5 py-4">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                    <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-wrap leading-relaxed">{product.purchaseNotes}</p>
                  </div>
                ) : null}
              </div>
            ) : null}

            {canViewPrices && (
              <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
                {/* Header */}
                <div className="border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-5 py-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Wallet className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Payments</h3>
                      <p className="text-[11px] text-muted-foreground">Receivables, payables, and recorded transactions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const totalInvOutstanding = (product.invoices ?? []).reduce(
                        (acc, invoiceItem) => acc + getInvoiceAmounts(invoiceItem).outstanding,
                        0
                      );
                      if (totalInvOutstanding > 0.009) {
                        return (
                          <span className="rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                            {formatCurrency(totalInvOutstanding)} Due
                          </span>
                        );
                      }
                      if ((product.invoices?.length ?? 0) > 0) {
                        return (
                          <span className="rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                            Settled
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {(() => {
                  const scheduled = productPayments.filter(isScheduledProductPayment);
                  const coveredScheduledIds = buildInvoiceCoveredScheduledIds(scheduled);
                  const actual = productPayments
                    .filter(isActualProductPayment)
                    .filter((row) => !isInvoiceLineLevelActual(row, coveredScheduledIds));
                  const paymentAllocations = buildScheduledPaymentAllocations(
                    scheduled,
                    productPayments.filter(isActualProductPayment),
                  );
                  const invoices = product.invoices ?? [];
                  const receivables = scheduled.filter((row) => row.direction !== "credit");
                  const payables = scheduled.filter((row) => row.direction === "credit");
                  const partnerAllocations = (product.dealPartners ?? []).filter(
                    (partner) => !partner.isCompanyOwner
                      && partner.partyId != null
                      && Number(partner.investmentAmount) > 0,
                  );
                  const hasMultipleDealPartners = partnerAllocations.length > 1;
                  const receivableInvoiceCount = hasMultipleDealPartners ? 0 : invoices.length;
                  const partnerInvestmentReferences = new Set([
                    "deal_partner",
                    "deal_partner_base",
                    "deal_partner_cost_line",
                  ]);
                  const nonInvoiceReceivables = receivables.filter((row) =>
                    !coveredScheduledIds.has(row.id)
                    && !(hasMultipleDealPartners && partnerInvestmentReferences.has(row.reference_type || "")),
                  );
                  const nonInvoicePayables = payables.filter((row) => !coveredScheduledIds.has(row.id));

                  const resolveInvoiceInvestor = (invoiceItem: (typeof invoices)[number]) => {
                    const linkedReceivable = receivables.find((row) => {
                      if (!coveredScheduledIds.has(row.id) || !row.party_id) return false;
                      const effectiveInvoiceId =
                        row.product_invoice_id
                        ?? row.invoice_id
                        ?? (row.purchase_cost_line_id != null
                          ? product.purchaseCostLines?.find((l) => l.id === row.purchase_cost_line_id)?.invoiceId
                          : null);
                      if (effectiveInvoiceId != null && String(effectiveInvoiceId) === String(invoiceItem.id)) {
                        return true;
                      }
                      // Deal-partner lines without invoice id: still linked if covered by this invoice's payables.
                      return payables.some(
                        (p) =>
                          String(p.product_invoice_id ?? "") === String(invoiceItem.id) &&
                          (p.notes || "").trim().toLowerCase() === (row.notes || "").trim().toLowerCase() &&
                          Math.abs(Number(p.amount) - Number(row.amount)) < 0.02,
                      );
                    });
                    if (linkedReceivable?.party_id) {
                      return {
                        id: Number(linkedReceivable.party_id),
                        name: linkedReceivable.party_name?.trim() || `Investor #${linkedReceivable.party_id}`,
                      };
                    }

                    const partners = (product.dealPartners ?? []).filter(
                      (dp) => dp.partyId != null && String(dp.partyId).trim() !== "",
                    );
                    const primary = partners[0];
                    if (primary?.partyId) {
                      return {
                        id: Number(primary.partyId),
                        name: primary.partyName?.trim() || `Investor #${primary.partyId}`,
                      };
                    }
                    return null;
                  };

                  const openInvoicePayDialog = (
                    invoiceItem: (typeof invoices)[number],
                    action: "pay" | "receive" = "pay",
                  ) => {
                    const amounts = getInvoiceAmounts(invoiceItem, action);
                    setPayDialogOutstanding(amounts.outstanding);
                    setPayDialogAction(action);

                    if (action === "receive") {
                      const investor = resolveInvoiceInvestor(invoiceItem);
                      setPayDialogPartyName(investor?.name || "Investor");
                      setPayDialogPartyId(investor?.id ?? 0);
                      setPayDialogTransactionType("investment");
                      setPayDialogAllowExcess(false);
                    } else {
                      setPayDialogPartyName(
                        invoiceItem.partyName || (isConsignment ? `Party #${invoiceItem.partyId}` : `Supplier #${invoiceItem.partyId}`),
                      );
                      setPayDialogPartyId(Number(invoiceItem.partyId));
                      setPayDialogTransactionType("purchase");
                      setPayDialogAllowExcess(false);
                    }

                    setPayDialogScheduledPaymentId(0);
                    setPayDialogInvoiceId(Number(invoiceItem.id));
                    setPayDialogInvoiceNo(invoiceItem.invoiceNo);
                    setPayDialogInvoiceDate(invoiceItem.invoiceDate);
                    setPayDialogInvoiceDueDate(invoiceItem.dueDate ?? null);

                    const items: Array<{ label: string; currencyAmount: number | null; currencyCode: string | null; amount: number }> = [];
                    const isBase = String(product.baseInvoiceId) === String(invoiceItem.id);
                    if (isBase && product.purchasePrice && product.purchasePrice > 0) {
                      items.push({
                        label: "Base Purchase Price",
                        currencyAmount: product.purchaseCurrencyAmount ?? null,
                        currencyCode: product.purchaseCurrency ?? null,
                        amount: product.purchasePrice,
                      });
                    }
                    (product.purchaseCostLines ?? []).forEach((l) => {
                      if (String(l.invoiceId) === String(invoiceItem.id)) {
                        items.push({
                          label: purchaseCostLineTitle(l),
                          currencyAmount: l.currencyAmount ?? null,
                          currencyCode: l.currencyCode ?? null,
                          amount: l.amount,
                        });
                      }
                    });
                    setPayDialogInvoiceCostItems(items.length > 0 ? items : undefined);

                    const hasForeignCurr = invoiceItem.currency && invoiceItem.currency !== invoiceItem.companyCurrency;
                    setPayDialogCurrencyCode(hasForeignCurr ? invoiceItem.currency! : null);
                    const rate = (hasForeignCurr && invoiceItem.amount && invoiceItem.amount > 0 && amounts.billed > 0)
                      ? Math.round((amounts.billed / invoiceItem.amount) * 10000) / 10000
                      : null;
                    setPayDialogExchangeRate(rate);
                    const foreignOutstanding = (hasForeignCurr && invoiceItem.amount && amounts.billed > 0)
                      ? Math.max(0, invoiceItem.amount - (amounts.paid * invoiceItem.amount / amounts.billed))
                      : null;
                    setPayDialogCurrencyAmount(foreignOutstanding ? Math.round(foreignOutstanding * 100) / 100 : null);
                    setPayDialogAllowExcess(false);
                    setPayDialogProfitDistributionId(null);
                    setPayDialogOpen(true);
                  };

                  const openScheduledPayDialog = (
                    row: (typeof scheduled)[number],
                    outstanding: number,
                    isPayable: boolean,
                    partyLabel: string,
                  ) => {
                    const scheduledInvoiceId =
                      row.product_invoice_id
                      ?? row.invoice_id
                      ?? (row.purchase_cost_line_id != null
                        ? product.purchaseCostLines?.find((l) => l.id === row.purchase_cost_line_id)?.invoiceId
                        : null)
                      ?? null;

                    setPayDialogOutstanding(outstanding);
                    setPayDialogAction(isPayable ? "pay" : "receive");
                    setPayDialogPartyName(partyLabel);
                    setPayDialogPartyId(row.party_id || 0);
                    setPayDialogTransactionType(row.transaction_type || "purchase");
                    setPayDialogScheduledPaymentId(row.id);
                    setPayDialogInvoiceId(scheduledInvoiceId != null ? Number(scheduledInvoiceId) : null);
                    setPayDialogInvoiceNo(null);
                    setPayDialogInvoiceDate(null);
                    setPayDialogInvoiceDueDate(null);
                    setPayDialogInvoiceCostItems(undefined);
                    setPayDialogCurrencyCode(row.currency_code || null);
                    setPayDialogCurrencyAmount(row.currency_amount ? Number(row.currency_amount) : null);
                    setPayDialogExchangeRate(row.exchange_rate ? Number(row.exchange_rate) : null);
                    // Excess/profit only for investment cost-line receives — never on payables.
                    const ref = row.reference_type || "";
                    setPayDialogAllowExcess(!isPayable && ref === "deal_partner_cost_line");
                    setPayDialogOpen(true);
                  };

                  const renderScheduledRow = (row: (typeof scheduled)[number]) => {
                    const partyLabel = row.party_name?.trim() || "Unknown party";
                    const isPayable = row.direction === "credit";
                    const totalMatched = paymentAllocations.get(row.id) ?? 0;
                    const outstanding = scheduledRowOutstanding(row, paymentAllocations);

                    let statusLabel = "";
                    let statusClass = "";

                    if (isPayable) {
                      if (outstanding <= 0.009) {
                        statusLabel = "Paid";
                        statusClass = "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20";
                      } else if (totalMatched > 0) {
                        statusLabel = "Partially Paid";
                        statusClass = "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20";
                      } else {
                        statusLabel = "Unpaid";
                        statusClass = "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20";
                      }
                    } else if (outstanding <= 0.009) {
                      statusLabel = "Received";
                      statusClass = "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20";
                    } else if (totalMatched > 0) {
                      statusLabel = "Partially Received";
                      statusClass = "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20";
                    } else {
                      statusLabel = "Uncollected";
                      statusClass = "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20";
                    }

                    const costTypeName =
                      row.cost_type_id != null
                        ? costTypeLabel.get(String(row.cost_type_id)) || "Cost item"
                        : (row.notes?.trim()
                          || row.transaction_type_label?.trim()
                          || (isPayable ? "Payable" : "Receivable"));

                    const effectiveInvoiceId =
                      row.product_invoice_id
                      ?? row.invoice_id
                      ?? (row.purchase_cost_line_id != null
                        ? product.purchaseCostLines?.find((l) => l.id === row.purchase_cost_line_id)?.invoiceId
                        : null);
                    const hasAttachedInvoice = effectiveInvoiceId != null && Number(effectiveInvoiceId) > 0;
                    const linkedInvoice = hasAttachedInvoice
                      ? product.invoices?.find((inv) => String(inv.id) === String(effectiveInvoiceId))
                      : null;

                    return (
                      <div
                        key={row.id}
                        className="rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">{partyLabel}</p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                              <p className="text-[11px] text-muted-foreground">
                                {costTypeName}
                              </p>
                              {hasAttachedInvoice && (
                                <span className="inline-flex items-center rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold text-primary">
                                  Invoice: {linkedInvoice?.invoiceNo || `#${effectiveInvoiceId}`}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="shrink-0 text-right space-y-1">
                            <p className="text-sm font-semibold tabular-nums text-foreground">
                              {formatCurrency(row.amount)}
                            </p>
                            {row.currency_amount != null && row.currency_code && row.currency_code !== getActiveCompanyCurrency().code ? (
                              <span className="text-[10px] text-muted-foreground tabular-nums block">
                                {formatCurrency(row.currency_amount, row.currency_code)} @ {row.exchange_rate ?? 1}
                              </span>
                            ) : null}
                            <div className="flex items-center justify-end gap-2">
                              <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", statusClass)}>
                                {statusLabel}
                              </span>
                              {outstanding > 0.009 && !hasAttachedInvoice && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-[10px] font-bold px-2.5"
                                  onClick={() => openScheduledPayDialog(row, outstanding, isPayable, partyLabel)}
                                >
                                  {isPayable ? "Pay" : "Receive"}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  };

                  const defaultTab =
                    (nonInvoiceReceivables.length > 0 || receivableInvoiceCount > 0 || hasMultipleDealPartners)
                      ? "receivables"
                      : (nonInvoicePayables.length > 0 || invoices.length > 0)
                        ? "payables"
                        : "history";

                  const renderInvoiceCard = (
                    invoiceItem: (typeof invoices)[number],
                    options?: { button?: "pay" | "receive" | null },
                  ) => {
                    const button = options?.button ?? "pay";
                    const mode = button === "receive" ? "receive" : "pay";
                    const amounts = getInvoiceAmounts(invoiceItem, mode);
                    const investor = mode === "receive" ? resolveInvoiceInvestor(invoiceItem) : null;
                    const partyRoleLabel = mode === "receive" ? "Investor" : (isConsignment ? "Party" : "Supplier");
                    const partyDisplayName =
                      mode === "receive"
                        ? (investor?.name || "—")
                        : (invoiceItem.partyName ||
                          (isConsignment ? `Party #${invoiceItem.partyId}` : `Supplier #${invoiceItem.partyId}`));

                    return (
                      <div
                        key={`invoice-${invoiceItem.id}-${button ?? "none"}`}
                        className="rounded-xl border border-border/50 bg-background/40 p-3.5 space-y-1 shadow-xs"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm text-foreground">
                                {invoiceItem.invoiceNo}
                              </span>
                              <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Invoice
                              </span>
                            </div>
                            <span className="text-[11px] text-muted-foreground block mt-1">
                              {partyRoleLabel}:{" "}
                              <strong className="text-foreground">{partyDisplayName}</strong>
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                            {amounts.outstanding <= 0.009 ? (
                              <span className="rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                                Settled
                              </span>
                            ) : (
                              <span className="rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                                Outstanding
                              </span>
                            )}
                            {(() => {
                              if (!invoiceItem.filePath || typeof invoiceItem.filePath !== "string") return null;
                              const raw = invoiceItem.filePath.trim();
                              const match = raw.match(/documents\/invoices\/([^/?#]+)/);
                              const fileUrl = match
                                ? `/storage/documents/invoices/${match[1]}`
                                : (normalizeProductMediaUrlForBrowser(raw) ?? raw);
                              const downloadFilename = invoiceItem.invoiceNo
                                ? `Invoice-${invoiceItem.invoiceNo.replace(/[/\\?%*:|"<>]/g, "-")}.pdf`
                                : (match ? match[1] : "invoice.pdf");

                              return (
                                <a
                                  href={fileUrl}
                                  download={downloadFilename}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline mt-1"
                                >
                                  <Download className="w-3 h-3" />
                                  Download PDF
                                </a>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-xs bg-muted/20 p-2.5 rounded-lg border border-border/30">
                          <div>
                            <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Billed</span>
                            <span className="font-bold text-foreground tabular-nums">
                              {invoiceItem.currency && invoiceItem.currency !== invoiceItem.companyCurrency && invoiceItem.amount != null && invoiceItem.amount > 0
                                ? `${invoiceItem.currency} ${invoiceItem.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : formatCurrency(amounts.billed)}
                            </span>
                            {invoiceItem.currency && invoiceItem.currency !== invoiceItem.companyCurrency && amounts.billed > 0 && (
                              <span className="text-[10px] text-muted-foreground tabular-nums block">
                                {formatCurrency(amounts.billed)}
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Outstanding</span>
                            {(() => {
                              const outstandingForeign = invoiceItem.currency && invoiceItem.currency !== invoiceItem.companyCurrency && invoiceItem.amount != null && invoiceItem.amount > 0
                                ? Math.max(0, invoiceItem.amount - (amounts.paid / (amounts.billed > 0 ? amounts.billed / invoiceItem.amount : 1)))
                                : null;
                              return (
                                <>
                                  <span className="font-bold text-primary tabular-nums">
                                    {outstandingForeign != null
                                      ? `${invoiceItem.currency} ${outstandingForeign.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : formatCurrency(amounts.outstanding)}
                                  </span>
                                  {outstandingForeign != null && amounts.outstanding > 0 && (
                                    <span className="text-[10px] text-muted-foreground tabular-nums block">
                                      {formatCurrency(amounts.outstanding)}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {invoiceItem.dueDate && (
                          <div className="text-[11px] text-muted-foreground flex justify-between">
                            <span>Due Date:</span>
                            <span className="font-medium text-foreground">{invoiceItem.dueDate}</span>
                          </div>
                        )}

                        {button && amounts.outstanding > 0.009 && (button !== "receive" || investor != null) && (
                          <Button
                            variant="default"
                            size="sm"
                            className="w-full text-xs font-bold gap-1.5 h-8 mt-1 shadow-xs"
                            onClick={() => openInvoicePayDialog(invoiceItem, button)}
                          >
                            {button === "receive" ? "Receive Invoice" : "Pay Invoice"}
                          </Button>
                        )}
                      </div>
                    );
                  };

                  return (
                    <Tabs defaultValue={defaultTab} className="w-full">
                      <div className="border-b border-border/40 px-5 pt-3 pb-2.5 bg-muted/10">
                        <TabsList className="grid w-full grid-cols-3 h-8 p-0.5 bg-muted/40">
                          <TabsTrigger value="receivables" className="text-xs data-[state=active]:bg-background">
                            Receivables {(nonInvoiceReceivables.length + receivableInvoiceCount + (hasMultipleDealPartners ? partnerAllocations.length : 0)) > 0 ? `(${nonInvoiceReceivables.length + receivableInvoiceCount + (hasMultipleDealPartners ? partnerAllocations.length : 0)})` : ""}
                          </TabsTrigger>
                          <TabsTrigger value="payables" className="text-xs data-[state=active]:bg-background">
                            Payables {(nonInvoicePayables.length + invoices.length) > 0 ? `(${nonInvoicePayables.length + invoices.length})` : ""}
                          </TabsTrigger>
                          <TabsTrigger value="history" className="text-xs data-[state=active]:bg-background">
                            History {actual.length > 0 ? `(${actual.length})` : ""}
                          </TabsTrigger>
                        </TabsList>
                      </div>

                      {/* Receivables — invoices (collective billed, net of discount) + scheduled rows */}
                      <TabsContent value="receivables" className="m-0 p-2 space-y-1 focus-visible:outline-none">
                        {paymentsLoading ? (
                          <p className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading receivables…
                          </p>
                        ) : receivableInvoiceCount === 0 && nonInvoiceReceivables.length === 0 && !hasMultipleDealPartners ? (
                          <div className="py-8 text-center">
                            <Calendar className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                            <p className="text-xs text-muted-foreground">No receivables to collect for this product.</p>
                          </div>
                        ) : (
                          <div className="space-y-1 max-h-[500px] overflow-y-auto pr-0.5">
                            {!hasMultipleDealPartners && invoices.map((invoiceItem) => renderInvoiceCard(invoiceItem, { button: "receive" }))}
                            {hasMultipleDealPartners && partnerAllocations.map((partner, index) => {
                              const partnerId = String(partner.partyId);
                              const partnerSchedules = receivables.filter((row) =>
                                partnerInvestmentReferences.has(row.reference_type || "")
                                && String(row.party_id ?? "") === partnerId,
                              );
                              const received = partnerSchedules.reduce(
                                (total, row) => total + (paymentAllocations.get(row.id) ?? 0),
                                0,
                              );
                              const investmentAmount = Number(partner.investmentAmount) || 0;
                              const outstanding = Math.max(0, investmentAmount - received);
                              const partyName = partner.partyName?.trim() || `Investor #${partner.partyId}`;
                              const status = outstanding <= 0.009
                                ? "Received"
                                : received > 0
                                  ? "Partially Received"
                                  : "Uncollected";
                              const statusClass = outstanding <= 0.009
                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"
                                : received > 0
                                  ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
                                  : "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20";

                              return (
                                <div
                                  key={`deal-partner-allocation-${String(partner.key || partner.partyId)}-${index}`}
                                  className="rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5"
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-foreground">{partyName}</p>
                                      <p className="mt-0.5 text-[11px] text-muted-foreground">Deal Partner Investment</p>
                                    </div>
                                    <div className="shrink-0 space-y-1 text-right">
                                      <p className="text-sm font-semibold tabular-nums text-foreground">
                                        {formatCurrency(investmentAmount)}
                                      </p>
                                      <div className="flex items-center justify-end gap-2">
                                        <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", statusClass)}>
                                          {status}
                                        </span>
                                        {outstanding > 0.009 && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2.5 text-[10px] font-bold"
                                            onClick={() => {
                                              setPayDialogOutstanding(outstanding);
                                              setPayDialogAction("receive");
                                              setPayDialogPartyName(partyName);
                                              setPayDialogPartyId(Number(partner.partyId));
                                              setPayDialogTransactionType("investment");
                                              setPayDialogScheduledPaymentId(0);
                                              setPayDialogInvoiceId(null);
                                              setPayDialogInvoiceNo(null);
                                              setPayDialogInvoiceDate(null);
                                              setPayDialogInvoiceDueDate(null);
                                              setPayDialogInvoiceCostItems(undefined);
                                              setPayDialogCurrencyCode(null);
                                              setPayDialogCurrencyAmount(null);
                                              setPayDialogExchangeRate(null);
                                              setPayDialogAllowExcess(false);
                                              setPayDialogProfitDistributionId(null);
                    setPayDialogOpen(true);
                                            }}
                                          >
                                            Receive
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {nonInvoiceReceivables.map((row) => renderScheduledRow(row))}
                          </div>
                        )}
                      </TabsContent>

                      {/* Payables — invoices + other obligations to pay */}
                      <TabsContent value="payables" className="m-0 p-2 space-y-1 focus-visible:outline-none">
                        {paymentsLoading ? (
                          <p className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading payables…
                          </p>
                        ) : invoices.length === 0 && nonInvoicePayables.length === 0 ? (
                          <div className="py-8 text-center">
                            <Receipt className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                            <p className="text-xs text-muted-foreground">No payables due for this product.</p>
                          </div>
                        ) : (
                          <div className="space-y-1 max-h-[500px] overflow-y-auto pr-0.5">
                            {invoices.map((invoiceItem) => renderInvoiceCard(invoiceItem, { button: "pay" }))}
                            {nonInvoicePayables.map((row) => renderScheduledRow(row))}
                          </div>
                        )}
                      </TabsContent>

                      {/* History — recorded transactions */}
                      <TabsContent value="history" className="m-0 p-2 space-y-1 focus-visible:outline-none">
                        {paymentsLoading ? (
                          <p className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading transactions…
                          </p>
                        ) : actual.length === 0 ? (
                          <div className="py-8 text-center">
                            <Wallet className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                            <p className="text-xs text-muted-foreground">
                              No payment transactions yet. They appear when purchase costs, partner investments, or customer payments are recorded.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-0.5">
                            {actual.map((row) => {
                              const partyLabel = row.party_name?.trim() || "Unknown party";
                              const isMoneyIn = row.credit > 0;

                              return (
                                <div
                                  key={row.id}
                                  className="rounded-lg border border-border/20 bg-muted/5 px-3 py-2 flex items-center justify-between gap-3 text-xs"
                                >
                                  <div className="min-w-0">
                                    <p className="font-medium text-foreground">{partyLabel}</p>
                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                      {row.payment_date && isValid(parseISO(row.payment_date))
                                        ? format(parseISO(row.payment_date), "PP")
                                        : (row.payment_date ?? "—")}
                                      {row.payment_method_label ? ` · ${row.payment_method_label}` : ""}
                                    </p>
                                    {row.notes && (
                                      <p className="text-[10px] text-muted-foreground mt-0.5 italic truncate">{row.notes}</p>
                                    )}
                                  </div>
                                  <div className="shrink-0 text-right space-y-1">
                                    <p className={cn("font-bold tabular-nums block", isMoneyIn ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400")}>
                                      {isMoneyIn ? "+" : "-"} {formatCurrency(row.amount)}
                                    </p>
                                    {row.currency_amount != null && row.currency_code && row.currency_code !== getActiveCompanyCurrency().code ? (
                                      <span className="text-[10px] text-muted-foreground tabular-nums block">
                                        {formatCurrency(row.currency_amount, row.currency_code)} @ {row.exchange_rate ?? 1}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  );
                })()}
              </div>
            )}

            {canViewPrices && product.latestSale && (
              <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
                <div className="border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-5 py-3.5 flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Sale Details</h3>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Invoice #{product.latestSale.invoice_no || product.latestSale.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {product.latestSale.outstanding > 0 && !product.latestSale.is_installment && (
                      <Button
                        type="button"
                        variant="premium"
                        size="sm"
                        className="h-7 gap-1 text-[10px] font-bold px-2 rounded-md shrink-0"
                        onClick={() => setBalanceDialogOpen(true)}
                      >
                        <Wallet className="h-3 w-3" />
                        Record Payment
                      </Button>
                    )}
                    <span className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider shrink-0",
                      product.latestSale.payment_status === "paid" 
                        ? "bg-emerald-500/10 border border-emerald-500/35 text-emerald-700 dark:text-emerald-300"
                        : "bg-amber-500/10 border border-amber-500/35 text-amber-700 dark:text-amber-300"
                    )}>
                      {product.latestSale.payment_status}
                    </span>
                  </div>
                </div>

                <div className="p-2 space-y-1">
                  {/* Customer Info */}
                  {product.latestSale.customer && (
                    <div className="rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5 space-y-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Customer</p>
                      <p className="text-sm font-semibold text-foreground">{product.latestSale.customer.name}</p>
                      {product.latestSale.customer.phone && (
                        <p className="text-xs text-muted-foreground">{product.latestSale.customer.phone}</p>
                      )}
                    </div>
                  )}

                  {/* Financial Summary (1 Row, Colored Indicators) */}
                  <div className="grid grid-cols-3 gap-2.5 text-xs">
                    <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.04] p-2.5 text-center">
                      <span className="text-muted-foreground block uppercase tracking-wider text-[9px] font-semibold mb-0.5">Sale Total</span>
                      <span className="font-bold text-blue-700 dark:text-blue-300 tabular-nums text-[13px]">{money(product.purchaseCurrency || undefined, product.latestSale.total_amount)}</span>
                    </div>
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-2.5 text-center">
                      <span className="text-muted-foreground block uppercase tracking-wider text-[9px] font-semibold mb-0.5">Paid Amount</span>
                      <span className="font-bold text-emerald-700 dark:text-emerald-300 tabular-nums text-[13px]">{money(product.purchaseCurrency || undefined, product.latestSale.paid_total)}</span>
                    </div>
                    <div className={cn(
                      "rounded-xl border p-2.5 text-center",
                      product.latestSale.outstanding > 0 
                        ? "border-rose-500/20 bg-rose-500/[0.04] text-rose-700 dark:text-rose-300"
                        : "border-emerald-500/20 bg-emerald-500/[0.04] text-emerald-700 dark:text-emerald-300"
                    )}>
                      <span className="text-muted-foreground block uppercase tracking-wider text-[9px] font-semibold mb-0.5">Outstanding</span>
                      <span className="font-bold tabular-nums text-[13px]">{money(product.purchaseCurrency || undefined, product.latestSale.outstanding)}</span>
                    </div>
                  </div>

                  {/* Dates Details */}
                  <div className="flex justify-between items-center text-xs border-t border-border/40 pt-3">
                    <div>
                      <span className="font-semibold text-[10px] uppercase tracking-wider text-muted-foreground mr-1.5">Sale Date:</span>
                      <span className="font-medium text-foreground">{product.latestSale.date || "—"}</span>
                    </div>
                    {product.latestSale.payable_date && product.latestSale.outstanding > 0 && (
                      <div className="text-right">
                        <span className="font-semibold text-[10px] uppercase tracking-wider text-rose-500 mr-1.5">Due Date:</span>
                        <span className="font-semibold text-rose-600 dark:text-rose-400">{product.latestSale.payable_date}</span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons (View Sale, Invoice, Gate Pass) */}
                  <div className="flex gap-2 border-t border-border/40 pt-3.5">
                    <Link to={`/product-sales/${product.latestSale.id}`} className="flex-1 min-w-0">
                      <Button
                        type="button"
                        variant="glass"
                        size="sm"
                        className="w-full gap-1.5 h-9 text-[11px] font-semibold rounded-xl border border-border/55 hover:bg-muted/40 transition-all shadow-sm px-2"
                      >
                        <Receipt className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="truncate">View Sale</span>
                      </Button>
                    </Link>
                    <a href={`/sales/${product.latestSale.id}/pdf`} target="_blank" rel="noreferrer" className="flex-1 min-w-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full gap-1.5 h-9 text-[11px] font-semibold rounded-xl border border-border/50 text-foreground hover:bg-muted/20 hover:text-foreground transition-all shadow-sm px-2"
                      >
                        <Printer className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">Invoice</span>
                      </Button>
                    </a>

                    {product.latestSale.payment_status === "paid" && (
                      <div className="flex-1 min-w-0">
                        {!product.latestSale.delivery_date ? (
                          canRecordPayment && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full gap-1.5 h-9 text-[11px] font-semibold rounded-xl border border-border/50 text-foreground hover:bg-muted/20 hover:text-foreground transition-all shadow-sm px-2"
                              onClick={openGatePassModal}
                            >
                              <Ticket className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="truncate">Gate Pass</span>
                            </Button>
                          )
                        ) : (
                          <a href={`/sales/${product.latestSale.id}/gate-pass/pdf`} target="_blank" rel="noreferrer">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full gap-1.5 h-9 text-[11px] font-semibold rounded-xl border border-border/50 text-foreground hover:bg-muted/20 hover:text-foreground transition-all shadow-sm px-2"
                            >
                              <Ticket className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="truncate">Gate Pass</span>
                            </Button>
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Update Gate Pass link if already generated */}
                  {product.latestSale.payment_status === "paid" && product.latestSale.delivery_date && canRecordPayment && (
                    <div className="text-right px-1" style={{ marginTop: "0px" }}>
                      <button
                        type="button"
                        className="text-[10px] font-semibold text-muted-foreground hover:text-primary transition-colors underline"
                        onClick={openGatePassModal}
                      >
                        Update Gate Pass details
                      </button>
                    </div>
                  )}

                  {/* Profit Distribution */}
                  {((product.latestSale.profit_distributions && product.latestSale.profit_distributions.length > 0) ||
                    Boolean(product.latestSale.profit_distribution_summary?.showroom)) && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Profit Distribution</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {/* Showroom charges */}
                        {product.latestSale.profit_distribution_summary?.showroom && (
                          <div className="rounded-xl border border-primary/20 bg-primary/[0.03] px-3.5 py-2.5 text-xs space-y-1.5">
                            <div className="flex justify-between items-start">
                              <span className="font-semibold text-foreground">
                                {product.latestSale.profit_distribution_summary.showroom.name || "Showroom Charges"}
                              </span>
                              <span className="inline-flex rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 text-[9px] font-bold uppercase border border-blue-500/20">
                                Showroom
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-muted-foreground">
                              <div>
                                <span>Amount: </span>
                                <span className="font-semibold text-foreground tabular-nums">
                                  {money(
                                    product.purchaseCurrency || undefined,
                                    Number(product.latestSale.profit_distribution_summary.showroom.amount ?? 0) +
                                      Number(product.latestSale.profit_distribution_summary.extra_profit ?? 0),
                                  )}
                                </span>
                              </div>
                              <div>
                                <span>Share: </span>
                                <span className="font-medium text-foreground tabular-nums">
                                  {product.latestSale.profit_distribution_summary.showroom.percent 
                                    ? `${Number(product.latestSale.profit_distribution_summary.showroom.percent).toFixed(2)}%` 
                                    : "—"}
                                </span>
                              </div>
                            </div>
                            {Number(product.latestSale.profit_distribution_summary.extra_profit ?? 0) > 0.009 ? (
                              <p className="text-[10px] text-amber-700 dark:text-amber-400">
                                Includes {money(
                                  product.purchaseCurrency || undefined,
                                  Number(product.latestSale.profit_distribution_summary.extra_profit),
                                )}{" "}
                                extra profit from cost recoveries
                              </p>
                            ) : null}
                          </div>
                        )}

                        {/* Partner distributions */}
                        {product.latestSale.profit_distributions && product.latestSale.profit_distributions.map((pd) => {
                          const summary = product.latestSale?.profit_distribution_summary;
                          let profitAmount = pd.profit_amount;
                          if (isOwnerSurplusSummary(summary) && pd.profit_amount <= 0) {
                            profitAmount = summary?.profit_summary?.owner_actual_profit ?? pd.profit_amount;
                          }
                          const percent = pd.profit_ratio ?? pd.purchase_ratio;

                          return (
                            <div key={pd.id} className="rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5 text-xs space-y-1.5">
                              <div className="flex justify-between items-start">
                                <span className="font-semibold text-foreground">{pd.party_name || "Deal Partner"}</span>
                                <span className={cn(
                                  "inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase",
                                  pd.status === "paid" 
                                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                                    : "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                                )}>
                                  {pd.status}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-muted-foreground">
                                <div>
                                  <span>Profit: </span>
                                  <span className="font-medium text-foreground tabular-nums">{money(product.purchaseCurrency || undefined, profitAmount)}</span>
                                </div>
                                <div>
                                  <span>Paid: </span>
                                  <span className="font-medium text-foreground tabular-nums">{money(product.purchaseCurrency || undefined, pd.paid_amount)}</span>
                                </div>
                                <div>
                                  <span>Balance: </span>
                                  <span className="font-semibold text-foreground tabular-nums">{money(product.purchaseCurrency || undefined, pd.balance)}</span>
                                </div>
                                <div>
                                  <span>Share: </span>
                                  <span className="font-medium text-foreground tabular-nums">{percent ? `${Number(percent).toFixed(2)}%` : "—"}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-muted-foreground">
                                <div>
                                  <span>Reinvested: </span>
                                  <span className="font-medium text-foreground tabular-nums">{money(product.purchaseCurrency || undefined, Number(pd.allocated_active ?? 0))}</span>
                                </div>
                                <div>
                                  <span>Available: </span>
                                  <span className="font-semibold text-foreground tabular-nums">{money(product.purchaseCurrency || undefined, Number(pd.available ?? pd.balance ?? 0))}</span>
                                </div>
                              </div>
                              {canRecordPayment && Number(pd.available ?? pd.balance ?? 0) > 0 && (
                                <div className="pt-2 border-t border-border/30 flex justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs px-2.5 font-medium border-primary/30 hover:bg-primary/10 hover:text-primary transition-all shadow-xs"
                                    onClick={() => {
                                      const available = Number(pd.available ?? pd.balance ?? 0);
                                      setPayDialogOutstanding(available > 0 ? available : Number(pd.balance ?? 0));
                                      setPayDialogAction("pay");
                                      setPayDialogPartyId(pd.party_id || 0);
                                      setPayDialogPartyName(pd.party_name || (product.dealType === "owner_vehicle" ? "Vehicle Owner / Investor" : "Deal Partner"));
                                      setPayDialogTransactionType("deal_partner");
                                      setPayDialogScheduledPaymentId(0);
                                      setPayDialogInvoiceId(null);
                                      setPayDialogCurrencyCode(null);
                                      setPayDialogCurrencyAmount(null);
                                      setPayDialogExchangeRate(null);
                                      setPayDialogInvoiceNo(null);
                                      setPayDialogInvoiceDate(null);
                                      setPayDialogInvoiceDueDate(null);
                                      setPayDialogInvoiceCostItems(undefined);
                                      setPayDialogAllowExcess(false);
                                      setPayDialogProfitDistributionId(Number(pd.id));
                                      setPayDialogOpen(true);
                                    }}
                                  >
                                    <CreditCard className="mr-1.5 h-3.5 w-3.5" />
                                    Pay {product.dealType === "owner_vehicle" ? "Investor / Owner" : "Partner"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Sale Payments */}
                  {product.latestSale.payments && product.latestSale.payments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sale Payments</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {product.latestSale.payments.map((p) => (
                          <div key={p.id} className="rounded-xl border border-border/40 bg-gradient-to-r from-background/50 to-muted/15 px-3.5 py-2.5 text-xs">
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <p className="font-semibold text-foreground">{p.transaction_type_label || "Payment"}</p>
                                <p className="text-[10px] text-muted-foreground">{p.payment_date || "—"} · {p.payment_method_label || "Other"}</p>
                              </div>
                              <span className="font-semibold text-foreground tabular-nums">{money(product.purchaseCurrency || undefined, p.amount)}</span>
                            </div>
                            {p.notes && (
                              <p className="mt-1 text-[10px] text-muted-foreground italic truncate">{p.notes}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}



            <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
              <div className="border-b border-border/40 bg-gradient-to-r from-muted/30 to-transparent px-5 py-3.5">
                <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">Meta</h3>
                <p className="mt-0.5 text-[11px] text-muted-foreground">Record details &amp; full product timeline</p>
              </div>
              <div className="p-2 space-y-1 text-sm">
                <div className="flex justify-between gap-4 rounded-lg border border-border/35 bg-muted/10 px-3 py-2">
                  <span className="text-muted-foreground">Created</span>
                  <span className="text-right text-foreground/90 tabular-nums">{new Date(product.createdAt).toLocaleString()}</span>
                </div>
                <div className="flex justify-between gap-4 rounded-lg border border-border/35 bg-muted/10 px-3 py-2">
                  <span className="text-muted-foreground">Category</span>
                  <span className="text-right text-foreground/90">{schema?.category.name ?? product.categoryId}</span>
                </div>
                <div className="flex items-center justify-between gap-4 rounded-lg border border-border/35 bg-muted/10 px-3 py-2">
                  <span className="text-muted-foreground">Inventory</span>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold uppercase tracking-wide",
                      inv.className,
                    )}
                  >
                    {inv.label}
                  </span>
                </div>
                <div className="flex justify-between gap-4 rounded-lg border border-border/35 bg-muted/10 px-3 py-2">
                  <span className="text-muted-foreground">Location</span>
                  <span className="text-right text-foreground/90 min-w-0 max-w-[65%] truncate" title={locationMetaLabel}>
                    {locationMetaLabel}
                  </span>
                </div>

                <div className="pt-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Product timeline
                  </p>
                  {(() => {
                    const filteredLogs = (product.inventoryStatusLogs ?? []).filter(
                      (log) => log.event !== "product_updated"
                    );
                    if (filteredLogs.length === 0) {
                      return (
                        <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/45 px-3 py-3">
                          No history recorded yet.
                        </p>
                      );
                    }
                    return (
                      <ol className="relative ms-2 space-y-0 border-s border-border/50 ps-4">
                        {filteredLogs.map((log, index) => {
                          const from = log.oldStatus ? inventoryStatusPresentation(log.oldStatus) : null;
                          const to = log.newStatus ? inventoryStatusPresentation(log.newStatus) : null;
                          const when = (() => {
                            if (!log.createdAt) return "—";
                            try {
                              const d = parseISO(log.createdAt);
                              return isValid(d) ? format(d, "PPp") : new Date(log.createdAt).toLocaleString();
                            } catch {
                              return log.createdAt;
                            }
                          })();
                          const showStatus =
                            Boolean(log.oldStatus || log.newStatus) &&
                            (log.oldStatus !== log.newStatus ||
                              log.event === "product_created" ||
                              log.event === "sold" ||
                              log.event === "unsold" ||
                              log.event === "reserved" ||
                              log.event === "inventory_status_changed" ||
                              log.event === "sale_cancelled");
                          return (
                            <li key={log.id} className="relative pb-2 last:pb-0">
                              <span
                                className={cn(
                                  "absolute -start-[1.3rem] top-1.5 flex h-2.5 w-2.5 rounded-full border-2 border-background",
                                  index === 0 ? "bg-primary" : "bg-muted-foreground/45",
                                )}
                                aria-hidden
                              />
                              <div className="rounded-lg border border-border/35 bg-muted/10 px-3 py-2.5 space-y-2">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="space-y-1.5 min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-foreground leading-snug">
                                      {log.eventLabel}
                                    </p>
                                    {showStatus ? (
                                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                        {from ? (
                                          <>
                                            <span
                                              className={cn(
                                                "inline-flex items-center rounded-md px-1.5 py-0.5 font-bold uppercase tracking-wide",
                                                from.className,
                                              )}
                                            >
                                              {from.label}
                                            </span>
                                            <span className="text-muted-foreground">→</span>
                                          </>
                                        ) : null}
                                        {to ? (
                                          <span
                                            className={cn(
                                              "inline-flex items-center rounded-md px-1.5 py-0.5 font-bold uppercase tracking-wide",
                                              to.className,
                                            )}
                                          >
                                            {to.label}
                                          </span>
                                        ) : (
                                          <span className="text-muted-foreground">—</span>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="text-right text-[10px] text-muted-foreground shrink-0 leading-normal">
                                    <div className="tabular-nums font-medium">{when}</div>
                                    <div className="truncate mt-0.5">{log.changedByName ?? "—"}</div>
                                  </div>
                                </div>
                                {log.reason ? (
                                  <p className="text-xs text-foreground/85 whitespace-pre-wrap leading-relaxed pt-1.5 border-t border-border/10">
                                    {log.reason}
                                  </p>
                                ) : null}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <ConfirmAlertDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this product?"
          description={
            <>
              Delete <strong className="text-foreground font-semibold">{product.name}</strong>? This cannot be undone.
            </>
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={async () => {
            if (!canDelete || productSold) {
              toast.error(productSold ? "Sold products cannot be deleted." : "You don’t have permission to delete products.");
              setConfirmDelete(false);
              return;
            }
            try {
              await deleteProduct(product.id);
              setConfirmDelete(false);
              toast.success("Product deleted successfully.");
              navigate("/products");
            } catch (e) {
              toast.error(catalogProductErrorMessage(e));
              setConfirmDelete(false);
            }
          }}
        />

        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-[min(96vw,1100px)] p-0 overflow-hidden bg-black border-border/40">
            <div className="relative">
              {activeLightbox?.url ? (
                <img
                  src={activeLightbox.url}
                  alt={activeLightbox.filename}
                  className="max-h-[80vh] w-full object-contain bg-black"
                />
              ) : (
                <div className="h-[60vh] w-full flex items-center justify-center text-sm text-muted-foreground bg-black">
                  No image
                </div>
              )}

              {lightboxImages.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white p-2 hover:bg-black/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                    onClick={() => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length)}
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white p-2 hover:bg-black/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                    onClick={() => setLightboxIndex((i) => (i + 1) % lightboxImages.length)}
                    aria-label="Next image"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              ) : null}

              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white px-4 py-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate">{activeLightbox?.filename ?? ""}</span>
                  {lightboxImages.length > 0 ? (
                    <span className="shrink-0 text-xs opacity-80 tabular-nums">
                      {lightboxIndex + 1} / {lightboxImages.length}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(viewingDocument)}
          onOpenChange={(open) => {
            if (!open) setViewingDocument(null);
          }}
        >
          <DialogContent className="flex max-h-[92vh] w-[96vw] max-w-5xl flex-col gap-0 overflow-hidden p-0">
            {viewingDocument ? (
              <>
                <div className="border-b border-border/50 bg-gradient-to-r from-muted/40 via-background to-transparent px-5 py-4 pr-12">
                  <DialogHeader className="space-y-1 text-left">
                    <DialogTitle className="flex items-center gap-2 text-base font-semibold truncate">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-background/80">
                        {isImageDocument(viewingDocument) ? (
                          <ImageIcon className="h-4 w-4 text-primary" />
                        ) : (
                          <FileText className="h-4 w-4 text-primary" />
                        )}
                      </span>
                      <span className="truncate">{viewingDocument.filename}</span>
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                      Document preview for {viewingDocument.filename}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-lg border border-border/45 bg-muted/20 px-3 py-2">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Type</p>
                      <p className="mt-0.5 truncate text-sm font-semibold text-foreground">
                        {viewingDocument.documentTypeName?.trim() || "—"}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/45 bg-muted/20 px-3 py-2">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Document Copy</p>
                      <p className="mt-0.5 text-sm font-semibold text-foreground">
                        {documentCopyLabel(viewingDocument.documentCopy)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/45 bg-muted/20 px-3 py-2">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">No. of Pages</p>
                      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
                        {viewingDocument.numberOfPages == null ? "—" : viewingDocument.numberOfPages}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/45 bg-muted/20 px-3 py-2">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Size</p>
                      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
                        {Math.round(viewingDocument.size / 1024)} KB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="min-h-0 flex-1 bg-muted/15 p-3 sm:p-4">
                  <div className="flex h-[min(62vh,720px)] items-center justify-center overflow-hidden rounded-xl border border-border/50 bg-background shadow-inner">
                    {viewingDocument.url && isImageDocument(viewingDocument) ? (
                      <img
                        src={viewingDocument.url}
                        alt={viewingDocument.filename}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : viewingDocument.url && isPdfDocument(viewingDocument) ? (
                      <iframe
                        title={viewingDocument.filename}
                        src={viewingDocument.url}
                        className="h-full w-full rounded-xl bg-white"
                      />
                    ) : (
                      <div className="flex max-w-sm flex-col items-center gap-3 px-6 py-10 text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border/50 bg-muted/30">
                          <FileText className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">Preview not available</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            This file type can’t be shown inline. Download it to open on your device.
                          </p>
                        </div>
                        {viewingDocument.url ? (
                          <Button asChild variant="outline" size="sm" className="gap-1.5">
                            <a href={viewingDocument.url} download={viewingDocument.filename}>
                              <Download className="h-3.5 w-3.5" />
                              Download file
                            </a>
                          </Button>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-border/50 bg-background/90 px-5 py-3">
                  {viewingDocument.url ? (
                    <Button asChild variant="outline" size="sm" className="gap-1.5">
                      <a href={viewingDocument.url} download={viewingDocument.filename}>
                        <Download className="h-3.5 w-3.5" />
                        Download
                      </a>
                    </Button>
                  ) : null}
                  <Button type="button" size="sm" onClick={() => setViewingDocument(null)}>
                    Close
                  </Button>
                </div>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {product?.latestSale && (
          <RecordBalancePaymentDialog
            open={balanceDialogOpen}
            onOpenChange={setBalanceDialogOpen}
            outstanding={product.latestSale.outstanding}
            onSubmit={async (payload) => {
              try {
                const r = await paySaleBalance(product.latestSale!.id, payload);
                toast.success(r.message);
                await reloadProduct();
              } catch (e: any) {
                toast.error(e.response?.data?.message || "Failed to record payment.");
                throw e;
              }
            }}
          />
        )}

        <GenerateGatePassDialog
          open={gatePassOpen}
          onOpenChange={setGatePassOpen}
          date={gatePassDate}
          time={gatePassTime}
          onDateChange={setGatePassDate}
          onTimeChange={setGatePassTime}
          saving={gatePassSaving}
          onSubmit={() => void submitGatePass()}
        />
      </div>
    </Layout>
  );
};

export default ProductView;

